import { JSX, createMemo, createSignal, Show } from 'solid-js';
import { Portal } from 'solid-js/web';
import {
  ViewDetailsResponse,
  TcTcpsApiService,
  TcRegion,
} from '@data-access/connect/tc-tcps';
import { t } from '@trimble/i18n';
import { useLogger } from '@trimble/solidjs-utils';
import { NotificationEventBus } from '@trimble/solidjs-notifications';
import { ViewsCapabilityProvider } from '@trimble/web3d-providers';
import {
  Cards,
  CardItem,
  DropDownOption,
  DeleteConfirmationModal,
} from '@tcweb/connect-ui';
import { UnreachableCaseError } from 'ts-essentials';
import { asError } from '@trimble/logging';
import { usePortalMount } from '../../../portal/PortalMountContext';
export type ViewCardItem = ViewDetailsResponse & CardItem;

export interface ViewCardsProps {
  views: ViewCardItem[];
  viewsProvider?: ViewsCapabilityProvider;
  tcTcpsApi: TcTcpsApiService;
  region: TcRegion;
  projectId: string;
  onCardClick?(view: ViewDetailsResponse): void;
  onViewDetailsSelect?(view: ViewDetailsResponse): void;
  deletePointCloudAssociationsForView?(
    projectId: string,
    viewId: string
  ): Promise<void>;
  deleteReservedViewData?(viewId: string): Promise<void>;
  onViewDeleted?(viewId: string): void;
  onViewDeleteError?(viewId: string, error: Error): void;
}

enum ViewAction {
  ViewDetails = 'view-details',
  Delete = 'delete',
}

export const ViewCards = (props: ViewCardsProps): JSX.Element => {
  const logger = useLogger().getLogger('ViewCards');
  const portalMount = usePortalMount();
  const [showDeleteConfirm, setShowDeleteConfirm] = createSignal(false);
  const [isDeleting, setIsDeleting] = createSignal(false);
  const [viewToDelete, setViewToDelete] =
    createSignal<ViewDetailsResponse | null>(null);

  const dropdownOptions: Record<ViewAction, DropDownOption> = {
    [ViewAction.ViewDetails]: {
      text: t('siteVisualization.sidePanelLayout.seeDetails'),
    },
    [ViewAction.Delete]: {
      text: t('general.delete'),
    },
  };

  const cardItems = createMemo((): CardItem[] => {
    return props.views.map((view) => {
      return {
        ...view,
        dropdownOptions: Object.values(dropdownOptions),
      };
    });
  });

  const handleViewOptionSelected = (
    cardId: string,
    option: string,
    _: number
  ): void => {
    const view = props.views.find((view) => view.id === cardId);
    if (!view) {
      return;
    }

    const action = (
      Object.entries(dropdownOptions) as [ViewAction, DropDownOption][]
    ).find(([, opt]) => opt.text === option)?.[0];

    if (!action) {
      logger.error('Unknown view action for option', { option });
      return;
    }

    switch (action) {
      case ViewAction.ViewDetails:
        props.onViewDetailsSelect?.(view);
        break;
      case ViewAction.Delete:
        setViewToDelete(view);
        setShowDeleteConfirm(true);
        break;
      default:
        throw new UnreachableCaseError(action);
    }
  };

  const handleDeleteConfirm = async (): Promise<void> => {
    const view = viewToDelete();
    if (!view) {
      return;
    }

    setIsDeleting(true);

    try {
      await props.tcTcpsApi.deleteView(props.region, view.id, props.projectId);

      if (props.deleteReservedViewData) {
        try {
          await props.deleteReservedViewData(view.id);
        } catch (e: unknown) {
          logger.warn(
            'Failed to delete reserved view data',
            { viewId: view.id },
            asError(e)
          );
        }
      }

      if (props.deletePointCloudAssociationsForView) {
        try {
          await props.deletePointCloudAssociationsForView(
            props.projectId,
            view.id
          );
        } catch (e: unknown) {
          logger.warn(
            'Failed to delete point cloud associations',
            { projectId: props.projectId, viewId: view.id },
            asError(e)
          );
        }
      }

      props.onViewDeleted?.(view.id);

      NotificationEventBus.emit({
        type: 'info',
        message: t('mobileMapping.views.deleteSuccess'),
      });
    } catch (error: unknown) {
      props.onViewDeleteError?.(view.id, asError(error));

      NotificationEventBus.emit({
        type: 'error',
        message: t('general.unhandledError'),
      });
      logger.error(
        'Failed to delete view',
        { projectId: props.projectId, region: props.region, view },
        asError(error)
      );
    } finally {
      setIsDeleting(false);
      setShowDeleteConfirm(false);
      setViewToDelete(null);
    }
  };

  const handleViewClick = async (viewId: string): Promise<void> => {
    try {
      const fullView = await props.tcTcpsApi.getViewDetails(
        viewId,
        props.region
      );

      if (props.viewsProvider) {
        await props.viewsProvider.loadView(fullView, props.region);
      }

      props.onCardClick?.(fullView);
    } catch (error: unknown) {
      logger.error('Failed to load view', { viewId }, asError(error));
    }
  };

  return (
    <div class="mx-2">
      <Cards
        cards={cardItems()}
        clickable={true}
        className="[&_.row]:m-0 [&_.row]:grid [&_.row]:grid-cols-[repeat(2,minmax(120px,1fr))] [&_.row]:gap-4"
        columnClasses="[&_.media-object]:h-auto [&_.media-object]:m-0 [&_.thumbnail-wrapper]:aspect-[16/9]"
        onCardClick={(card) => void handleViewClick(card.id)}
        onDropdownOptionSelect={(cardId, option, index) => {
          handleViewOptionSelected(cardId, option, index);
        }}
      />

      <Show when={showDeleteConfirm()}>
        <Portal mount={portalMount()}>
          <DeleteConfirmationModal
            title={t('mobileMapping.views.deleteViewTitle')}
            message={t('mobileMapping.views.confirmDelete')}
            waiting={isDeleting()}
            onCancel={() => setShowDeleteConfirm(false)}
            onConfirm={() => void handleDeleteConfirm()}
            mount={portalMount()}
          />
        </Portal>
      </Show>
    </div>
  );
};
